import React, { useState, useEffect } from "react";
import {
  Link,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Icon } from "@iconify/react";

import { useAuth } from "../../contexts/AuthContext";
import ProtectedRoute from "../../components/ProtectedRoute";

import AttendanceLocation from "./Master/AttendanceLocation";
import LeaveType from "./Master/LeaveType";

// Payroll imports
import PaymentSetup from "./Payroll/PaymentSetup";
import SalaryMaster from "./Payroll/SalaryMaster";
import Reimbursements from "./Payroll/Reimbursements";
import Allowances from "./Payroll/Allowances";
import Deductions from "./Payroll/Deductions";
import IncomeTax from "./Payroll/IncomeTax";
import ProfessionalTax from "./Payroll/ProfessionalTax";

import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import "../../styles/InnerMenu.scss";
import "./Admin.scss";

const Admin = () => {
  const { isAuthorized, isLoading, canAccessModule } = useAuth();
  const [visibleSubmenu, setVisibleSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Menu items with module-based access control
  const menuItems = [
    {
      name: "Leave Types",
      routes: ["/admin/leave-types"],
      auth: {
        customCheck: () =>
          canAccessModule("LEAVE") ||
          canAccessModule("ATTENDANCE") ||
          canAccessModule("EMAIL_AUTOMATION") ||
          canAccessModule("PAYROLL"),
      },
    },

    {
      name: "Attendance Location",
      routes: ["/admin/attendance-location"],
      auth: {
        customCheck: () =>
          canAccessModule("LEAVE") ||
          canAccessModule("ATTENDANCE") ||
          canAccessModule("EMAIL_AUTOMATION") ||
          canAccessModule("PAYROLL"),
      },
    },

    {
      name: "Payroll",
      subItems: [
        "Salary Master",
        "Payment Setup",
        "Reimbursements",
        "Allowances",
        "Deductions",
        "Income Tax",
        "Professional Tax",
      ],
      routes: [
        "/admin/salary-master",
        "/admin/payment-setup",
        "/admin/reimbursements",
        "/admin/allowances",
        "/admin/deductions",
        "/admin/income-tax",
        "/admin/professional-tax",
      ],
      auth: {
        customCheck: () => canAccessModule("PAYROLL"),
      },
    },
  ];

  // Filter menu items based on custom auth checks
  const filteredMenuItems = menuItems.filter((item) => {
    if (item.auth.customCheck) {
      return item.auth.customCheck();
    }
    return isAuthorized(item.auth);
  });

  useEffect(() => {
    const activeIndex = filteredMenuItems.findIndex((item) =>
      item.routes.some((route) => location.pathname.startsWith(route))
    );

    if (activeIndex !== -1) setVisibleSubmenu(activeIndex);
  }, [location.pathname, filteredMenuItems]);

  const handleMenuClick = (index) => {
    const item = filteredMenuItems[index];
    const isAlreadyOpen = visibleSubmenu === index;

    setVisibleSubmenu(isAlreadyOpen ? null : index);

    if (item.routes && item.routes.length > 0 && !isAlreadyOpen) {
      navigate(item.routes[0]);
    }
  };

  const getDefaultRedirect = () => {
    if (filteredMenuItems.length > 0) {
      const firstItem = filteredMenuItems[0];
      return firstItem.routes[0];
    }
    return "/admin";
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <div className="custom-ess-container">
      {/* TOP MENU */}
      <div className="custom-menu">
        {filteredMenuItems.map((item, index) => {
          const isActive =
            visibleSubmenu === index ||
            item.routes.some((route) => location.pathname.startsWith(route));

          return (
            <div
              key={item.name}
              className={`custom-menu-item ${isActive ? "custom-active" : ""}`}
              onClick={() => handleMenuClick(index)}
            >
              {item.subItems ? (
                <>
                  {item.name}
                  <span className="arrow-icon">
                    <Icon
                      icon={
                        isActive
                          ? "ic:round-expand-less"
                          : "ic:round-expand-more"
                      }
                    />
                  </span>
                </>
              ) : (
                <Link to={item.routes[0]} className="custom-menu-link">
                  {item.name}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* PAYROLL SUBMENU */}
      {visibleSubmenu !== null &&
        filteredMenuItems[visibleSubmenu]?.subItems && (
          <div className="custom-submenu-container">
            <div className="custom-analytics-submenu">
              {filteredMenuItems[visibleSubmenu].subItems.map(
                (subItem, subIndex) => {
                  const route =
                    filteredMenuItems[visibleSubmenu].routes[subIndex];

                  return (
                    <Link
                      key={subItem}
                      to={route}
                      className={`custom-submenu-item ${
                        location.pathname === route ? "custom-active" : ""
                      }`}
                    >
                      {subItem}
                    </Link>
                  );
                }
              )}
            </div>
          </div>
        )}

      {/* MAIN CONTENT ROUTES */}
      <div className="custom-content">
        <Routes>
          <Route
            path="/leave-types"
            element={
              <ProtectedRoute
                customCheck={(auth) =>
                  auth.canAccessModule("LEAVE") ||
                  auth.canAccessModule("ATTENDANCE") ||
                  auth.canAccessModule("EMAIL_AUTOMATION") ||
                  auth.canAccessModule("PAYROLL")
                }
              >
                <LeaveType />
              </ProtectedRoute>
            }
          />

          <Route
            path="/attendance-location"
            element={
              <ProtectedRoute
                customCheck={(auth) =>
                  auth.canAccessModule("LEAVE") ||
                  auth.canAccessModule("ATTENDANCE") ||
                  auth.canAccessModule("EMAIL_AUTOMATION") ||
                  auth.canAccessModule("PAYROLL")
                }
              >
                <AttendanceLocation />
              </ProtectedRoute>
            }
          />

          {/* PAYROLL ROUTES */}
          <Route
            path="/salary-master"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <SalaryMaster />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment-setup"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <PaymentSetup />
              </ProtectedRoute>
            }
          />

          <Route
            path="/reimbursements"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <Reimbursements />
              </ProtectedRoute>
            }
          />

          <Route
            path="/allowances"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <Allowances />
              </ProtectedRoute>
            }
          />

          <Route
            path="/deductions"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <Deductions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/income-tax"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <IncomeTax />
              </ProtectedRoute>
            }
          />

          <Route
            path="/professional-tax"
            element={
              <ProtectedRoute
                customCheck={(auth) => auth.canAccessModule("PAYROLL")}
              >
                <ProfessionalTax />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={<Navigate to={getDefaultRedirect()} replace />}
          />
        </Routes>
      </div>
    </div>
  );
};

export default Admin;
